import {
  type SyntheticEvent,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box, Button, Snackbar, Typography } from '@mui/material';
import { CircularProgress } from '@mui/material';
import {
  ArrowForwardIosOutlined,
  ErrorOutlineOutlined,
  RequestPageOutlined,
  MoreVertSharp,
  CheckCircleOutlineRounded,
  InfoOutlined,
  CalendarToday,
} from '@mui/icons-material';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { DateTime } from 'luxon';

import {
  TripLegStatus,
  TripLegStatusBC,
  tripStatusAttestationList,
} from '../../../data/models/trip-leg-status.model';
import { TripLegExceptionBC } from '../../../data/models/trip-leg-exception.model';
import type {
  TripLegPackageDetail,
  TripLeg,
  TripLegResponseItem,
} from '../../../data/models/trip-leg.model';

import * as tripPackageInventorySlice from '../../../data/tripPackageInventory.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as tripSlice from '../../../data/trip.slice';
import * as referenceSlice from '../../../data/reference.slice';

import ExpireTime from './ExpireTime';
import GenerateInvoiceDialog from './GenerateInvoiceDialog';
import NeedsAttentionDialog from './NeedsAttentionDialog';

import { financialUtils } from '../../../services/financialUtils';
import { attestationUtils } from '../../../services/attestationUtils';
import {
  selectTripLegEvent,
  setTripLegEvent,
  selectTripLegExceptionEvent,
  setTripLegExceptionEvent,
  selectTripLegDiscussionEvent,
  setTripLegDiscussionEvent,
  selectLastTripLegEventRefreshAt,
  setLastTripLegEventRefreshAt,
  selectPendingExceptionTripLegIDs,
} from '../../../data/trip.slice';

const PackageIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none">
      <path
        d="M30.72 7.1634L17.1815 0.283024C16.8198 0.0973785 16.4133 0 16 0C15.5867 0 15.1802 0.0973785 14.8185 0.283024L1.28 7.16626C0.893367 7.36274 0.570621 7.65204 0.345464 8.00394C0.120308 8.35584 0.0010004 8.75743 0 9.16678V22.8332C0.0010004 23.2426 0.120308 23.6442 0.345464 23.9961C0.570621 24.348 0.893367 24.6373 1.28 24.8337L14.8185 31.717C15.1802 31.9026 15.5867 32 16 32C16.4133 32 16.8198 31.9026 17.1815 31.717L30.72 24.8337C31.1066 24.6373 31.4294 24.348 31.6545 23.9961C31.8797 23.6442 31.999 23.2426 32 22.8332V9.16821C31.9998 8.75813 31.8809 8.35565 31.6557 8.00293C31.4305 7.65021 31.1073 7.36024 30.72 7.1634ZM16 2.28355L28.36 8.57091L23.78 10.9001L11.4185 4.61273L16 2.28355ZM16 14.8583L3.64 8.57091L8.85538 5.91879L21.2154 12.2062L16 14.8583ZM2.46154 10.5714L14.7692 16.8274V29.0863L2.46154 22.8347V10.5714ZM29.5385 22.8289L17.2308 29.0863V16.8331L22.1538 14.331V19.4309C22.1538 19.7341 22.2835 20.0249 22.5143 20.2392C22.7451 20.4536 23.0582 20.5741 23.3846 20.5741C23.711 20.5741 24.0241 20.4536 24.2549 20.2392C24.4857 20.0249 24.6154 19.7341 24.6154 19.4309V13.0792L29.5385 10.5714V22.8275V22.8289Z"
        fill="#011012"
        fillOpacity="0.54"
      />
    </svg>
  );
};

const StyledSnackbar = styled(Snackbar)({
  '& .MuiSnackbarContent-root': {
    borderRadius: 3,
    background:
      'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), #2E7D32',
    boxShadow: '0px 2px 7px 0px rgba(1, 16, 18, 0.15)',
    color: '#2E7D32',
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    display: 'flex',
    width: 326,
    padding: '6px 16px 8px 0px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
});

const PREFIX = 'Task';

const classes = {
  list: `${PREFIX}-list`,
  item: `${PREFIX}-item`,
  header: `${PREFIX}-header`,
  icon: `${PREFIX}-icon`,
  text: `${PREFIX}-text`,
  errorText: `${PREFIX}-error-text`,
  content: `${PREFIX}-content`,
  contentItem: `${PREFIX}-content-item`,
  action: `${PREFIX}-action`,
  subTitle: `${PREFIX}-subTitle`,
  dueDateContainer: `${PREFIX}-due-date-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.list}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
    color: 'rgba(1, 16, 18, 0.90)',
  },
  [`& .${classes.item}`]: {
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    borderRadius: 8,
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  [`& .${classes.icon}`]: {
    width: 36,
    height: 36,
    fill: theme.palette.grey[600],
    marginRight: 15,
  },
  [`& .${classes.text}`]: {
    fontWeight: 700,
    fontFamily: 'Bitter',
    overflow: 'hidden',
    fontSize: 21,
  },
  [`& .${classes.errorText}`]: {
    fontWeight: 700,
    fontFamily: 'Bitter',
    fontSize: 21,
  },
  [`& .${classes.subTitle}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 12,
    lineHeight: '266%',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: theme.spacing(-0.25),
  },
  [`& .${classes.dueDateContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.error.main,
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  [`& .${classes.contentItem}`]: {
    width: theme.spacing(12),
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.action}`]: {
    width: theme.spacing(6),
    minWidth: theme.spacing(6),
    height: theme.spacing(6),
    borderRadius: '100%',
    backgroundColor: theme.palette.grey[300],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fill: theme.palette.grey[600],
    },
  },
  [`& .${classes.icon}`]: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    '& svg': {
      color: theme.palette.grey[600],
    },
  },
}));

const StyledInfoOutlined = styled(InfoOutlined)(({ theme }) => ({
  width: '20px',
  height: '20px',
  marginRight: theme.spacing(0.375),
  color: theme.palette.error.main,
}));

export default function TaskList({ providerId }: { providerId: number }) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [needsAttentionDialogOpen, setNeedsAttentionDialogOpen] =
    useState(false);
  const [generateInvoiceDialogOpen, setGenerateInvoiceDialogOpen] =
    useState(false);
  const [serviceStartTime, setServiceStartTime] = useState<string>();
  const [soonestExpiring, setSoonestExpiring] = useState<DateTime>();
  const [tripCount, setTripCount] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [hasRequestedTripLegs, setHasRequestedTripLegs] = useState(false);
  const [hasSeenTripLegsLoading, setHasSeenTripLegsLoading] = useState(false);
  const [lockTripLegsWidget, setLockTripLegsWidget] = useState(false);

  const tripLegExceptionItems = useAppSelector(
    referenceSlice.selectTripLegExceptions,
  );
  const pendingExceptionTripLegIDs = useAppSelector(
    selectPendingExceptionTripLegIDs,
  );
  const tripLegEvent = useAppSelector(selectTripLegEvent);
  const tripLegExceptionEvent = useAppSelector(selectTripLegExceptionEvent);
  const tripLegDiscussionEvent = useAppSelector(selectTripLegDiscussionEvent);
  const lastTripLegEventRefreshAt = useAppSelector(
    selectLastTripLegEventRefreshAt,
  );
  const loadedTripLegs = useAppSelector(tripSlice.selectLoadedTripLegs);
  const loadingTripLegs = useAppSelector(tripSlice.selectLoadingTripLegs);

  const handleSnackbarClose = (_: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const fetchOptimizedTripLegs = () => {
    dispatch(
      tripSlice.fetchTripLegsV2Async({
        transportationProviderID: [providerId.toString()],
        attested: false,
        includeTripLegStatusLogs: true,
        includeTripLegAddOnLineItems: true,
        page: 1,
        pageSize: 1000,
        tripLegStatusBCs: [...tripStatusAttestationList],
        pickupStartDate: attestationUtils.getStartDate().toUTC().toISO(),
        pickupEndDate: DateTime.now().endOf('day').toUTC().toISO(),
      }),
    );
  };

  const handleInvoiceGenerated = () => {
    // Display success and clean up the states
    setSnackbarOpen(true);
    dispatch(providerSlice.clearAttestationQueue());
    dispatch(providerSlice.setSubmittingAttestation(false));
    dispatch(providerSlice.setSubmittedAttestation(false));
    setGenerateInvoiceDialogOpen(false);
    fetchOptimizedTripLegs();
  };

  useEffect(() => {
    if (providerId) {
      const now = DateTime.now();
      dispatch(tripPackageInventorySlice.getOfferedPackagesAsync(providerId));
      dispatch(tripPackageInventorySlice.getTripServiceConfigurationAsync());
      setHasRequestedTripLegs(true);
      setHasSeenTripLegsLoading(false);
      setLockTripLegsWidget(false);
      dispatch(setLastTripLegEventRefreshAt(null));
      fetchOptimizedTripLegs();
    }
    dispatch(referenceSlice.fetchTripLegExceptionsAsync());
    dispatch(referenceSlice.fetchChargeTypesAsync());
  }, [providerId]);

  useEffect(() => {
    if (hasRequestedTripLegs && loadingTripLegs) {
      setHasSeenTripLegsLoading(true);
    }
  }, [hasRequestedTripLegs, loadingTripLegs]);

  useEffect(() => {
    if (loadedTripLegs) {
      setLockTripLegsWidget(true);
    }
  }, [loadedTripLegs]);

  const offeredPackages = useAppSelector(
    tripPackageInventorySlice.selectOfferedPackages,
  );

  useEffect(() => {
    const tripLegs = offeredPackages.reduce((acc, { tripLegs }) => {
      acc.push(...tripLegs);
      return acc;
    }, new Array<TripLeg>());

    if (tripLegs.length > 0) {
      dispatch(
        tripPackageInventorySlice.fetchTripLegsTotalValueAsync(
          tripLegs,
          providerId,
        ),
      );
    } else {
      dispatch(tripPackageInventorySlice.setTripLegsTotalValue(0));
    }
    checkValidity();
  }, [offeredPackages]);

  const tripLegsTotalValue = useAppSelector(
    tripPackageInventorySlice.selectTripLegsTotalValue,
  );

  const checkValidity = useCallback(() => {
    const earliestExpirationUTC = offeredPackages.reduce(
      (prev: string | null, current: TripLegPackageDetail) => {
        if (!prev) {
          return current.tripLegPackageOfferingExpirationDateTimeUTC;
        } else if (
          current.tripLegPackageOfferingExpirationDateTimeUTC.localeCompare(
            prev,
          ) < 0
        ) {
          return current.tripLegPackageOfferingExpirationDateTimeUTC;
        }
        return prev;
      },
      null,
    );

    const allTripLegsInPackages = offeredPackages.reduce((acc, tripPackage) => {
      acc.push(...tripPackage.tripLegs);
      return acc;
    }, new Array<TripLeg>());

    const earliestDueDateForTripPackages = allTripLegsInPackages.reduce(
      (earliest, tripLeg) => {
        if (!tripLeg.appointmentDate) return earliest;
        return !earliest || tripLeg.appointmentDate < earliest
          ? tripLeg.appointmentDate
          : earliest;
      },
      null as string | null,
    );

    const tripLegCount = allTripLegsInPackages.length;

    const expiration = earliestExpirationUTC
      ? DateTime.fromISO(earliestExpirationUTC)
      : undefined;
    setServiceStartTime(
      earliestDueDateForTripPackages
        ? DateTime.fromISO(earliestDueDateForTripPackages).toFormat(
            'MM/dd/yyyy',
          )
        : '--/--/--',
    );
    setSoonestExpiring(expiration);
    setTripCount(tripLegCount);
  }, [offeredPackages]);

  const allTripLegs = useAppSelector(tripSlice.selectTripLegs);

  const showTripLegsLoading =
    hasRequestedTripLegs && !loadedTripLegs && !lockTripLegsWidget;

  const showTripLegsContent =
    hasRequestedTripLegs &&
    hasSeenTripLegsLoading &&
    (loadedTripLegs || lockTripLegsWidget);

  const tripLegsSorted = useMemo(() => {
    const items = [...allTripLegs.items];
    // Filter out trip legs with future pickUpDueTimeUTC
    const filteredItems = items.filter((tripLeg) => {
      return tripLeg.appointmentTimeUTC
        ? DateTime.fromISO(tripLeg.appointmentTimeUTC) < DateTime.now()
        : DateTime.fromISO(tripLeg.appointmentDate) <
            DateTime.now().startOf('day');
    });
    // Sort by pickUpDueTimeUTC, but if pickUpDueTimeUTC is null, sort by appointmentDate
    // to make sure Will Call trips are sorted appropriately
    return {
      ...allTripLegs,
      items: filteredItems.sort((a, b) => {
        if (a.pickUpDueTimeUTC && b.pickUpDueTimeUTC) {
          return a.pickUpDueTimeUTC.localeCompare(b.pickUpDueTimeUTC);
        }
        if (a.pickUpDueTimeUTC && !b.pickUpDueTimeUTC) {
          return a.pickUpDueTimeUTC.localeCompare(b.appointmentDate);
        }
        if (!a.pickUpDueTimeUTC && b.pickUpDueTimeUTC) {
          return a.appointmentDate.localeCompare(b.pickUpDueTimeUTC);
        }
        return a.appointmentDate.localeCompare(b.appointmentDate);
      }),
    };
  }, [allTripLegs]);

  const isWithinNext2Days = (dateStr: string): boolean => {
    const currentYear = DateTime.now().get('year');
    const targetDate = DateTime.fromFormat(
      `${currentYear}/${dateStr}`,
      'yyyy/MM/dd',
    ).startOf('day');
    const today = DateTime.now().startOf('day');
    const in2Days = DateTime.now().plus({ days: 2 }).startOf('day');

    return targetDate >= today && targetDate <= in2Days;
  };

  const isMissingInfo = (tripLeg: TripLegResponseItem): boolean => {
    if (!tripLeg.tripLegStatusLogs) return true;
    if (!tripLeg.driverFirstName) return true;

    const status = tripLeg.tripLegStatusBC;
    if (status === TripLegStatusBC.Completed) {
      const requiredStatuses = [
        TripLegStatusBC.Onsite,
        TripLegStatusBC.Onboard,
        TripLegStatusBC.Completed,
      ];

      const statusLogs = tripLeg.tripLegStatusLogs.map(
        (log) => log.tripLegStatusBC,
      );

      return !requiredStatuses.every((status) => statusLogs.includes(status));
    }

    if (status === TripLegStatusBC.NoLoad) {
      const requiredStatuses = [TripLegStatusBC.Onsite, TripLegStatusBC.NoLoad];

      const statusLogs = tripLeg.tripLegStatusLogs.map(
        (log) => log.tripLegStatusBC,
      );

      return !requiredStatuses.every((status) => statusLogs.includes(status));
    }

    if (status === TripLegStatusBC.CanceledLate) {
      return false;
    }

    return true;
  };

  const hasReadyInvoiceException = (tripLeg: TripLegResponseItem): boolean => {
    return Boolean(
      tripLegExceptionItems.find(
        (item) => item.tripLegExceptionBC === TripLegExceptionBC.ReadyInvoice,
      )?.tripLegExceptionID === tripLeg.tripLegExceptionID,
    );
  };

  // Trips that need attention include anything with missing information, is
  // under discussion, or has the exception READYINVOICE. All other exceptions
  // should be ignored. Trips that are have status of ACCEPTED should only be
  // included if the appointment time is in the past. If it's a will call trip,
  // it should be included if the appointment date is in the past.
  // Will Call trips without pickup time (not activated) should be excluded.
  const tripLegsThatNeedAttention = useMemo(() => {
    return tripLegsSorted.items.filter((tripLeg) => {
      // Exclude Will Call trips that haven't been activated (no pickup time)
      if (tripLeg.isWillCall && !tripLeg.pickUpDueTimeUTC) {
        return false;
      }
      const hasException =
        pendingExceptionTripLegIDs.includes(tripLeg.tripLegID) ||
        (tripLeg.isException && hasReadyInvoiceException(tripLeg));
      const hasMissingInfo = isMissingInfo(tripLeg);
      const isUnderDiscussion = tripLeg.isUnderDiscussion;
      const hasNeedsAttentionState =
        hasMissingInfo || isUnderDiscussion || hasException;

      const isCompleted = tripLeg.tripLegStatusBC === TripLegStatusBC.Completed;
      const isOnsite = tripLeg.tripLegStatusBC === TripLegStatusBC.Onsite;
      const isOnboard = tripLeg.tripLegStatusBC === TripLegStatusBC.Onboard;
      const isNoLoad = tripLeg.tripLegStatusBC === TripLegStatusBC.NoLoad;
      const isCanceledLate =
        tripLeg.tripLegStatusBC === TripLegStatusBC.CanceledLate;
      const isAcceptedAndPastTime =
        tripLeg.tripLegStatusBC === TripLegStatusBC.Accepted &&
        DateTime.fromISO(tripLeg.appointmentTimeUTC) < DateTime.now();
      const isWillCallAndPastTime =
        hasNeedsAttentionState &&
        tripLeg.isWillCall &&
        DateTime.fromISO(tripLeg.appointmentDate) <=
          DateTime.now().startOf('day');
      return (
        hasNeedsAttentionState ||
        isOnsite ||
        isOnboard ||
        isAcceptedAndPastTime ||
        isWillCallAndPastTime
      );
    });
  }, [tripLegsSorted, pendingExceptionTripLegIDs]);

  // Subset of trips from tripLegsThatNeedAttention that have missing information
  const numberOfTripLegsWithMissingInfo = useMemo(() => {
    return tripLegsThatNeedAttention.filter((tripLeg) => {
      const today = DateTime.now().toFormat('yyyy-MM-dd');
      const pickupTime = DateTime.fromISO(tripLeg.pickUpDueTimeUTC);
      const sevenDaysAgo = attestationUtils.getStartDate();
      if (pickupTime < sevenDaysAgo) return false;
      if (
        tripLeg.isWillCall &&
        DateTime.fromISO(tripLeg.appointmentDate).toFormat('yyyy-MM-dd') ===
          today
      ) {
        return false;
      }
      const isMissing = isMissingInfo(tripLeg);
      return isMissing;
    }).length;
  }, [tripLegsThatNeedAttention]);

  const earliestDueDateForNeedsAttention = useMemo(() => {
    const earliestTrip = tripLegsThatNeedAttention.reduce(
      (earliest, tripLeg) => {
        if (!tripLeg.appointmentDate) return earliest;
        return !earliest || tripLeg.appointmentDate < earliest.appointmentDate
          ? tripLeg
          : earliest;
      },
      null as TripLegResponseItem | null,
    );

    return earliestTrip
      ? DateTime.fromISO(earliestTrip.appointmentDate)
          .plus({ days: 7 })
          .toFormat('MM/dd')
      : '';
  }, [tripLegsThatNeedAttention]);

  const tripLegsUnderDiscussion = useMemo(() => {
    return tripLegsThatNeedAttention.filter(
      (tripLeg) => tripLeg.isUnderDiscussion,
    );
  }, [tripLegsThatNeedAttention]);

  // Trip legs for the Generate Invoice widget, which should only include
  // completed, no load, canceled late, or accepted trips that have an
  // appointment time in the past, or will call trips that have an appointment
  // date in the past. They should also not have missing information, be under
  // discussion, or have the exception READYINVOICE.
  const tripLegsWithoutException = useMemo(() => {
    return tripLegsSorted.items.filter((tripLeg) => {
      const hasException =
        pendingExceptionTripLegIDs.includes(tripLeg.tripLegID) ||
        (tripLeg.isException && hasReadyInvoiceException(tripLeg));
      const hasMissingInfo = isMissingInfo(tripLeg);
      const isUnderDiscussion = tripLeg.isUnderDiscussion;
      const isTripLegInvoiced = tripLeg.isTripLegInvoiced;
      const canInvoice =
        !hasMissingInfo &&
        !isUnderDiscussion &&
        !hasException &&
        !isTripLegInvoiced;
      const isCompleted = tripLeg.tripLegStatusBC === TripLegStatusBC.Completed;
      const isNoLoad = tripLeg.tripLegStatusBC === TripLegStatusBC.NoLoad;
      const isCanceledLate =
        tripLeg.tripLegStatusBC === TripLegStatusBC.CanceledLate;
      return (isCompleted || isNoLoad || isCanceledLate) && canInvoice;
    });
  }, [tripLegsSorted, pendingExceptionTripLegIDs]);

  // To get the Friday date that comes two weeks after the Friday of the week containing
  const earliestDueDateForTripsWithoutException = useMemo(() => {
    const earliestTrip = tripLegsWithoutException.reduce(
      (earliest, trip) => {
        if (!trip.appointmentDate) return earliest;
        return !earliest || trip.appointmentDate < earliest.appointmentDate
          ? trip
          : earliest;
      },
      null as TripLegResponseItem | null,
    );

    if (!earliestTrip?.appointmentDate) return '';

    return earliestTrip
      ? DateTime.fromISO(earliestTrip.appointmentDate)
          .plus({ days: 7 })
          .toFormat('MM/dd')
      : '';
  }, [tripLegsWithoutException]);

  const totalPrice = tripLegsWithoutException.reduce((sum, trip) => {
    const baseAmount = financialUtils.parsePrice(
      (trip.payableAmount as string) ?? '0',
    );
    const addOnTotal =
      trip.tripLegAddOnLineItems?.reduce((addOnSum, addOn) => {
        return addOnSum + financialUtils.parsePrice(addOn.lineItemAmount);
      }, 0) || 0;
    const totalTripPrice = baseAmount + addOnTotal;
    const roundedPrice = financialUtils.roundTo(sum + totalTripPrice, 2);
    return roundedPrice;
  }, 0);

  const totalPriceFormatted = financialUtils.formatPrice(totalPrice);

  const handleNeedsAttention = useCallback(() => {
    setNeedsAttentionDialogOpen(true);
  }, []);

  const closeNeedsAttentionDialog = useCallback(() => {
    setNeedsAttentionDialogOpen(false);
  }, []);

  const handleTripLegs = useCallback(() => {
    navigate('/tripPackages');
  }, [navigate]);

  const handleGenerateInvoice = useCallback(() => {
    setGenerateInvoiceDialogOpen(true);
  }, []);

  const closeGenerateInvoiceDialog = useCallback(() => {
    setGenerateInvoiceDialogOpen(false);
  }, []);

  useEffect(() => {
    if (tripLegsWithoutException.length === 0) {
      setGenerateInvoiceDialogOpen(false);
    }
  }, [tripLegsWithoutException.length]);

  useEffect(() => {
    if (tripLegsThatNeedAttention.length === 0) {
      setNeedsAttentionDialogOpen(false);
    }
  }, [tripLegsThatNeedAttention.length]);

  useEffect(() => {
    if (tripLegEvent) {
      if (
        tripLegEvent === TripLegStatus.Accepted ||
        tripLegEvent === TripLegStatus.Onsite ||
        tripLegEvent === TripLegStatus.Onboard ||
        tripLegEvent === TripLegStatus.Completed ||
        tripLegEvent === TripLegStatus.CanceledLate ||
        tripLegEvent === TripLegStatus.NoLoad ||
        tripLegEvent === TripLegStatus.TripLegAppointmentTimePassed
      ) {
        const now = DateTime.now();
        const lastFetchAt = lastTripLegEventRefreshAt
          ? DateTime.fromISO(lastTripLegEventRefreshAt)
          : null;
        const canRefresh =
          !lastFetchAt || now.diff(lastFetchAt, 'minutes').as('minutes') >= 2;
        if (canRefresh) {
          setTimeout(() => {
            fetchOptimizedTripLegs();
          }, 1000);
          dispatch(setLastTripLegEventRefreshAt(now.toISO()));
        }
      }
      dispatch(setTripLegEvent(null));
    }
  }, [tripLegEvent, lastTripLegEventRefreshAt]);

  useEffect(() => {
    if (tripLegExceptionEvent) {
      setTimeout(() => {
        fetchOptimizedTripLegs();
      }, 1000);
      dispatch(setTripLegExceptionEvent(false));
    }
  }, [tripLegExceptionEvent]);

  useEffect(() => {
    if (tripLegDiscussionEvent) {
      setTimeout(() => {
        fetchOptimizedTripLegs();
      }, 1000);
      dispatch(setTripLegDiscussionEvent(false));
    }
  }, [tripLegDiscussionEvent]);

  return (
    <StyledBox className={classes.list}>
      <Box className={classes.item} onClick={handleTripLegs}>
        <Box className={classes.icon}>
          <PackageIcon />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: 208,
          }}>
          <Typography variant="caption" className={classes.subTitle}>
            {t('status.serviceStart')}
          </Typography>
          <Typography variant="h5" className={classes.text}>
            {serviceStartTime}
          </Typography>
        </Box>
        <Box className={classes.contentItem} sx={{ width: 96 }}>
          <Typography variant="caption" className={classes.subTitle}>
            {t('status.trips')}
          </Typography>
          <Typography variant="h5" className={classes.text}>
            {tripCount}
          </Typography>
        </Box>
        <Box className={classes.contentItem} sx={{ width: 96 }}>
          <Typography variant="caption" className={classes.subTitle}>
            {t('status.value')}
          </Typography>
          <Typography variant="h5" className={classes.text}>
            {tripLegsTotalValue
              ? financialUtils.formatPrice(tripLegsTotalValue)
              : '0'}
          </Typography>
        </Box>
        <Box className={classes.contentItem} sx={{ flex: 1 }}>
          <Typography variant="caption" className={classes.subTitle}>
            {t('status.expires')}
          </Typography>
          <Typography variant="h5" className={classes.text}>
            <ExpireTime
              soonestExpiringTime={soonestExpiring}
              onExpire={checkValidity}
            />
          </Typography>
        </Box>
        <Box className={classes.action}>
          <MoreVertSharp />
        </Box>
      </Box>
      {showTripLegsLoading ? (
        <Box className={classes.item}>
          <Box className={classes.icon}>
            <ErrorOutlineOutlined fontSize="large" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="h5" className={classes.text}>
              {t('status.needsAttention')}
            </Typography>
          </Box>
        </Box>
      ) : showTripLegsContent && tripLegsThatNeedAttention.length > 0 ? (
        <Box className={classes.item} onClick={handleNeedsAttention}>
          <Box className={classes.icon}>
            <ErrorOutlineOutlined fontSize="large" />
          </Box>
          <Box sx={{ width: 208 }}>
            <Typography variant="h5" className={classes.text}>
              {t('status.needsAttention')}
            </Typography>
          </Box>
          <Box className={classes.contentItem} sx={{ width: 96 }}>
            <Typography variant="caption" className={classes.subTitle}>
              {t('status.missingInfo')}
            </Typography>
            <Typography variant="h5" className={classes.text}>
              {numberOfTripLegsWithMissingInfo}
            </Typography>
          </Box>
          <Box className={classes.contentItem} sx={{ width: 96 }}>
            <Typography variant="caption" className={classes.subTitle}>
              {t('status.discuss')}
            </Typography>
            <Typography variant="h5" className={classes.text}>
              {tripLegsUnderDiscussion.length}
            </Typography>
          </Box>
          <Box className={classes.contentItem} sx={{ width: 96 }}>
            <Typography variant="caption" className={classes.subTitle}>
              {t('status.due')}
            </Typography>
            {isWithinNext2Days(earliestDueDateForNeedsAttention) ? (
              <Box className={classes.dueDateContainer}>
                <StyledInfoOutlined />
                <Typography variant="h5" className={classes.errorText}>
                  {earliestDueDateForNeedsAttention}
                </Typography>
              </Box>
            ) : (
              <Typography variant="h5" className={classes.text}>
                {earliestDueDateForNeedsAttention}
              </Typography>
            )}
          </Box>
          <Box className={classes.contentItem} sx={{ flex: 1 }} />
          <Button className={classes.action}>
            <ArrowForwardIosOutlined />
          </Button>
        </Box>
      ) : (
        <></>
      )}
      {showTripLegsLoading ? (
        <Box className={classes.item}>
          <Box className={classes.icon}>
            <RequestPageOutlined fontSize="large" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="h5" className={classes.text}>
              {t('status.generateInvoice')}
            </Typography>
          </Box>
        </Box>
      ) : showTripLegsContent && tripLegsWithoutException.length ? (
        <Box className={classes.item} onClick={handleGenerateInvoice}>
          <Box className={classes.icon}>
            <RequestPageOutlined fontSize="large" />
          </Box>
          <Box sx={{ width: 208 }}>
            <Typography variant="h5" className={classes.text}>
              {t('status.generateInvoice')}
            </Typography>
          </Box>
          <Box className={classes.contentItem} sx={{ width: 96 }}>
            <Typography variant="caption" className={classes.subTitle}>
              {t('status.trips')}
            </Typography>
            <Typography variant="h5" className={classes.text}>
              {tripLegsWithoutException.length}
            </Typography>
          </Box>
          <Box className={classes.contentItem} sx={{ width: 96 }}>
            <Typography variant="caption" className={classes.subTitle}>
              {t('status.value')}
            </Typography>
            <Typography variant="h5" className={classes.text}>
              {totalPriceFormatted}
            </Typography>
          </Box>
          <Box className={classes.contentItem} sx={{ width: 96 }}>
            <Typography variant="caption" className={classes.subTitle}>
              {t('status.due')}
            </Typography>
            {isWithinNext2Days(earliestDueDateForTripsWithoutException) ? (
              <Box className={classes.dueDateContainer}>
                <StyledInfoOutlined />
                <Typography variant="h5" className={classes.errorText}>
                  {earliestDueDateForTripsWithoutException}
                </Typography>
              </Box>
            ) : (
              <Typography variant="h5" className={classes.text}>
                {earliestDueDateForTripsWithoutException}
              </Typography>
            )}
          </Box>
          <Box className={classes.contentItem} sx={{ flex: 1 }} />
          <Box className={classes.action}>
            <ArrowForwardIosOutlined />
          </Box>
        </Box>
      ) : (
        <></>
      )}
      <NeedsAttentionDialog
        key={'needsAttentionDialog'}
        open={needsAttentionDialogOpen}
        onClose={closeNeedsAttentionDialog}
        providerId={providerId}
        tripLegs={tripLegsThatNeedAttention}
      />
      <GenerateInvoiceDialog
        open={generateInvoiceDialogOpen}
        onClose={closeGenerateInvoiceDialog}
        onInvoiceGenerated={handleInvoiceGenerated}
        tripLegs={tripLegsWithoutException}
      />
      <Box>
        <StyledSnackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          message={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineRounded color="success" />
              <Typography variant="body2">
                {t('snackbar.invoiceGenerated')}
              </Typography>
            </Box>
          }
        />
      </Box>
    </StyledBox>
  );
}
